/**
 * @unscoped — WorkspacePage 의 i18n — Cycle 52 (도입) + Cycle 57 (FileGrid 확장).
 *
 * cycle 26 drift-i18n / cycle 37 admin-i18n / cycle 41 base-i18n / cycle 50 approval-i18n /
 * cycle 51 calendar-i18n 통합 패턴 일관.
 *
 * 정책 (cycle 46/49 학습 적용):
 *   - fallback chain: lang → en → FALLBACK_LANG (ko)
 *   - 전수 non-empty 가드는 본 모듈 test 가 강제
 *   - cycle 57 — params 치환 옵션 (calendar-i18n/mail-i18n/onboarding-i18n 의 일관 패턴)
 */

import {
  getSharedLang, setSharedLang, subscribeSharedLang,
  FALLBACK_LANG, type Lang,
} from './base-i18n';

/* ════════════════════════════════════════════════════════════════════════
 * ★★★ `WorkspaceI18nKey` 에 ⇒ ⭐ «눈»을 준다 — 2026-10-12 (⇒ 꼴 통일 ★ 둘째 통)
 *
 *   바로 아래 ⇒ 선언에서 ★ 명시 타입을 뗐고 · 닫는 자리 ⇒ ★ `satisfies` 로 다시 걸었다.
 *   ⇒ ★ 까닭 ⇒ 선언에 `Record<string, …>` 가 붙으면 ⇒ ⭐ `keyof` 가 ★ `string` 이 된다
 *     ⇒ ⇒ ⛔ 즉 `tWorkspace(아무글자)` 가 ★ tsc 를 통과했다.
 *
 * ⭐ 이 통의 수 (2026-10-12) ⇒ ★ 애너테이션을 떼고 tsc ⇒ ⭐ 오류 ★ 0건
 *   ⇒ ⭐ 분모 ⇒ ★ 부르는 자리 ⭐ 366 (제품 34파일 · 리터럴 329 · 삼항 18 · 변수 15 · ⛔ 단언 4)
 *     ⇒ ⇒ ✅ 즉 ★ 「이미 깨끗」이다 — ⇒ ⛔ 「부르는 데가 없다」가 아니다.
 *   ⇒ ⚠ 단언 ★ 4 자리는 ⇒ ⭐ 이 눈이 ★ «비켜 가는» 자리다 ⇒ ⇒ 그 넷은 ★ 따로 걷는다(아직 안 했다).
 *
 * ⇒ ★ 불변식·까닭·재는 법은 ⇒ ⭐ `mail-i18n.ts` 머리말에 ★ 한 번만 적었다(⇒ 여기 되풀이하지 않는다).
 *   ⇒ ⇒ ⭐ 그 한 줄만 옮겨 적으면 ⇒ ★ 「사전 선언에 `: Record<string, …>` 가 ⭐ 없다」가 ★ 불변식이고,
 *     ⇒ ⇒ ⛔ 「`satisfies` 글자가 있나」로 재지 않는다. ⇒ ★ 아래 시험의 ⭐ 한 칸이 그것을 잡는다.
 * ════════════════════════════════════════════════════════════════════════ */
const MESSAGES = {
  // ----- WorkspacePage 메인 -----
  /* ── 화면 머리 (정본 A3 · 2026-09-08) ────────────────────────────── */
  'workspace.head.desc_home': { ko: '최근에 손댄 파일과 폴더입니다.', en: 'Recent files and folders.', ja: '最近使ったファイルとフォルダーです。' },
  'workspace.head.desc_my': { ko: '내가 올린 파일과 폴더입니다.', en: 'Files and folders you own.', ja: '自分がアップロードしたファイルとフォルダーです。' },
  'workspace.head.desc_shared': { ko: '다른 사람이 나에게 공유한 것입니다.', en: 'Shared with you by others.', ja: '他の人から共有されたものです。' },
  'workspace.head.desc_shared_by_me': { ko: '내가 다른 사람에게 공유한 것입니다.', en: 'What you shared with others.', ja: '自分が他の人に共有したものです。' },
  'workspace.head.desc_company': { ko: '회사가 함께 쓰는 문서입니다.', en: 'Documents shared across the company.', ja: '全社で共有する文書です。' },
  'workspace.head.desc_trash': { ko: '치운 파일입니다 — 되살리거나 완전히 지울 수 있습니다.', en: 'Deleted files — restore or remove for good.', ja: '削除したファイルです。復元も完全削除もできます。' },
  'workspace.head.help_title': { ko: '이 화면의 규칙', en: 'How this screen works', ja: 'この画面のルール' },
  'workspace.head.help_drag': {
    ko: '파일을 끌어 폴더에 놓으면 옮겨집니다. 바깥에서 끌어 놓으면 올라갑니다.',
    en: 'Drag a file onto a folder to move it. Drop from your computer to upload.',
    ja: 'ファイルをフォルダーにドラッグすると移動します。外部からのドロップはアップロードです。',
  },
  'workspace.head.help_share': {
    ko: '공유는 사람마다 «보기» 와 «편집» 을 따로 줍니다. 내가 공유한 것은 「공유됨」에서 되돌립니다.',
    en: 'Sharing grants view or edit per person. Undo yours from “Shared”.',
    ja: '共有は人ごとに「閲覧」と「編集」を分けて付与します。自分の共有は「共有済み」で解除します。',
  },
  'workspace.head.help_trash': {
    ko: '치운 파일은 휴지통에 남고, 용량은 완전히 지워야 돌아옵니다.',
    en: 'Deleted files stay in Trash; storage frees up only after permanent deletion.',
    ja: '削除したファイルはゴミ箱に残ります。容量は完全削除後に戻ります。',
  },
  'workspace.title': { ko: '워크스페이스', en: 'Workspace', ja: 'ワークスペース' },
  'workspace.subtitle': {
    ko: '팀과 함께 사용하는 파일과 문서',
    en: 'Files and documents shared with your team',
    ja: 'チームと共有するファイルと文書',
  },
  'workspace.aria.storage': { ko: '스토리지 사용량', en: 'Storage usage', ja: 'ストレージ使用量' },
  'workspace.storage.title': { ko: '스토리지 사용량', en: 'Storage usage', ja: 'ストレージ使用量' },
  'workspace.storage.meta_pending': {
    ko: '측정 준비 중 — backend quota endpoint 후 연동',
    en: 'Measurement pending — awaiting backend quota endpoint',
    ja: '測定準備中 — backend quota endpoint 待ち',
  },
  'workspace.storage.remaining_pending': { ko: '측정 준비 중', en: 'Pending', ja: '準備中' },
  'workspace.aria.breadcrumb': { ko: '브레드크럼', en: 'Breadcrumb', ja: 'ブレッドクラム' },
  'workspace.err.fetch_failed': { ko: '폴더 조회 실패', en: 'Failed to load folder', ja: 'フォルダ取得失敗' },
  'workspace.err.folder_denied': {
    ko: '이 폴더를 열 권한이 없습니다.',
    en: 'You do not have permission to open this folder.',
    ja: 'このフォルダを開く権限がありません。',
  },
  'workspace.btn.retry': { ko: '다시 시도', en: 'Retry', ja: '再試行' },

  // ----- FolderTree -----
  'workspace.tree.aria': { ko: '폴더 트리', en: 'Folder tree', ja: 'フォルダツリー' },
  // 좌측 패널 접기 (2026-09-07 · 정본 A8 채택). 공용 컴포넌트가 한글을 조합하지 않게
  'workspace.tree.loading': { ko: '로딩…', en: 'Loading…', ja: '読み込み中...' },
  'workspace.tree.empty': { ko: '폴더가 없습니다', en: 'No folders', ja: 'フォルダがありません' },
  'workspace.tree.aria_collapse': { ko: '닫기', en: 'Collapse', ja: '閉じる' },
  'workspace.tree.aria_expand': { ko: '열기', en: 'Expand', ja: '開く' },

  // ----- FolderCreateModal -----
  'workspace.modal.heading': { ko: '새 폴더 만들기', en: 'Create new folder', ja: '新規フォルダ作成' },
  'workspace.modal.aria_dialog': { ko: '새 폴더 만들기', en: 'Create new folder', ja: '新規フォルダ作成' },
  'workspace.modal.aria_close': { ko: '닫기', en: 'Close', ja: '閉じる' },
  'workspace.modal.label_name': { ko: '폴더 이름', en: 'Folder name', ja: 'フォルダ名' },
  'workspace.modal.btn_cancel': { ko: '취소', en: 'Cancel', ja: 'キャンセル' },
  'workspace.modal.btn_submit_idle': { ko: '만들기', en: 'Create', ja: '作成' },
  'workspace.modal.btn_submit_loading': { ko: '저장 중…', en: 'Saving…', ja: '保存中...' },
  'workspace.modal.err.empty_name': { ko: '폴더 이름을 입력하세요.', en: 'Please enter a folder name.', ja: 'フォルダ名を入力してください。' },
  'workspace.modal.err.save_failed': { ko: '저장 실패', en: 'Save failed', ja: '保存失敗' },

  // ----- FileListView (table headers) -----
  'workspace.list.col.name': { ko: '이름', en: 'Name', ja: '名前' },
  'workspace.list.col.size': { ko: '크기', en: 'Size', ja: 'サイズ' },
  'workspace.list.col.type': { ko: '유형', en: 'Type', ja: '種別' },
  'workspace.list.col.uploader': { ko: '업로더', en: 'Uploader', ja: 'アップローダー' },
  'workspace.list.col.created_at': { ko: '생성일', en: 'Created', ja: '作成日' },
  'workspace.list.empty': { ko: '이 폴더가 비어 있습니다', en: 'This folder is empty', ja: 'このフォルダは空です' },
  'workspace.list.type.folder': { ko: '폴더', en: 'Folder', ja: 'フォルダ' },

  // ----- StorageBreakdown 카테고리 -----
  'workspace.storage.cat.doc': { ko: '문서', en: 'Documents', ja: '文書' },
  'workspace.storage.cat.image': { ko: '이미지', en: 'Images', ja: '画像' },
  'workspace.storage.cat.video': { ko: '영상', en: 'Videos', ja: '動画' },
  'workspace.storage.cat.other': { ko: '기타', en: 'Other', ja: 'その他' },
  'workspace.storage.aria_breakdown': { ko: '카테고리별 사용량', en: 'Usage by category', ja: 'カテゴリ別使用量' },

  // ===== Cycle 57 — FileGrid =====
  'workspace.grid.loading': { ko: '로딩…', en: 'Loading…', ja: '読み込み中...' },
  'workspace.grid.empty': {
    ko: '이 폴더가 비어 있습니다',
    en: 'This folder is empty',
    ja: 'このフォルダは空です',
  },
  'workspace.grid.type_folder': { ko: '폴더', en: 'Folder', ja: 'フォルダ' },
  'workspace.grid.unverified_tooltip': { ko: 'finalize 미완료', en: 'finalize incomplete', ja: 'finalize 未完了' },
  'workspace.grid.unverified_label': { ko: '·미검증', en: '·unverified', ja: '·未検証' },
  'workspace.grid.delete_folder_aria': {
    ko: '{name} 폴더 삭제',
    en: 'Delete folder {name}',
    ja: 'フォルダ {name} を削除',
  },

  // ===== 2026-08-12 — 파일 업로드 =====
  'workspace.btn.new_folder': { ko: '새 폴더', en: 'New folder', ja: '新規フォルダ' },
  'workspace.btn.new_file': { ko: '파일 업로드', en: 'Upload files', ja: 'ファイルアップロード' },
  'workspace.upload.heading': { ko: '파일 업로드', en: 'Upload files', ja: 'ファイルアップロード' },
  'workspace.upload.aria_dialog': { ko: '파일 업로드', en: 'Upload files', ja: 'ファイルアップロード' },
  'workspace.upload.label_pick': { ko: '올릴 파일 선택', en: 'Choose files', ja: 'アップロードするファイル' },
  'workspace.upload.hint_parent': { ko: '올라갈 위치', en: 'Destination', ja: 'アップロード先' },
  'workspace.upload.hint_max': {
    ko: '파일당 최대 200MB',
    en: 'Up to 200MB per file',
    ja: '1ファイル最大200MB',
  },
  'workspace.upload.btn_idle': { ko: '업로드', en: 'Upload', ja: 'アップロード' },
  'workspace.upload.btn_loading': { ko: '업로드 중…', en: 'Uploading…', ja: 'アップロード中...' },
  'workspace.upload.empty_pick': { ko: '올릴 파일을 선택하세요.', en: 'Please choose a file.', ja: 'ファイルを選択してください。' },
  'workspace.upload.drop_here': { ko: '여기에 놓으면 업로드합니다', en: 'Drop to upload', ja: 'ここにドロップしてアップロード' },
  'workspace.upload.err.quota': {
    ko: '스토리지 한도를 초과했습니다. 관리자에게 한도 상향을 요청하거나 파일을 정리해 주세요.',
    en: 'Storage limit exceeded. Ask an administrator to raise the limit, or remove some files.',
    ja: 'ストレージ上限を超えました。管理者に上限引き上げを依頼するか、ファイルを整理してください。',
  },
  'workspace.upload.err.too_large': {
    ko: '{name} 은(는) 200MB 를 넘어 올릴 수 없습니다.',
    en: '{name} exceeds the 200MB limit.',
    ja: '{name} は200MBを超えているためアップロードできません。',
  },
  'workspace.upload.err.failed': { ko: '{name} 업로드 실패: {reason}', en: 'Failed to upload {name}: {reason}', ja: '{name} のアップロードに失敗: {reason}' },
  'workspace.upload.result_ok': { ko: '{count}개 업로드 완료', en: '{count} file(s) uploaded', ja: '{count}件アップロード完了' },

  // ===== 2026-08-14 — 드라이브 좌측 메뉴 + 만들기 =====
  'workspace.create.button': { ko: '만들기 또는 업로드', en: 'New or upload', ja: '作成またはアップロード' },
  'workspace.create.aria': { ko: '만들기 메뉴', en: 'Create menu', ja: '作成メニュー' },
  'workspace.create.folder': { ko: '폴더', en: 'Folder', ja: 'フォルダー' },
  'workspace.create.upload_files': { ko: '파일 업로드', en: 'Upload files', ja: 'ファイルをアップロード' },
  'workspace.create.upload_folder': { ko: '폴더 업로드', en: 'Upload folder', ja: 'フォルダーをアップロード' },
  'workspace.create.new_doc': { ko: '새 문서', en: 'New document', ja: '新しいドキュメント' },
  'workspace.create.word': { ko: 'Word 문서', en: 'Word document', ja: 'Word 文書' },
  'workspace.create.excel': { ko: 'Excel 통합문서', en: 'Excel workbook', ja: 'Excel ブック' },
  'workspace.create.ppt': { ko: 'PowerPoint 프레젠테이션', en: 'PowerPoint presentation', ja: 'PowerPoint プレゼンテーション' },
  'workspace.create.markdown': { ko: 'Markdown 문서', en: 'Markdown document', ja: 'Markdown 文書' },
  'workspace.create.text': { ko: '텍스트 문서', en: 'Text document', ja: 'テキスト文書' },
  'workspace.create.name_prompt': { ko: '새 문서 이름', en: 'New document name', ja: '新しいドキュメント名' },
  'workspace.create.default_name': { ko: '제목 없는 문서', en: 'Untitled document', ja: '無題のドキュメント' },
  'workspace.create.failed': { ko: '문서를 만들지 못했습니다: {reason}', en: 'Could not create the document: {reason}', ja: 'ドキュメントを作成できませんでした: {reason}' },
  'workspace.nav.home': { ko: '홈', en: 'Home', ja: 'ホーム' },
  'workspace.nav.my': { ko: '내 파일', en: 'My files', ja: 'マイファイル' },
  'workspace.nav.shared': { ko: '공유됨', en: 'Shared', ja: '共有' },
  'workspace.nav.trash': { ko: '휴지통', en: 'Trash', ja: 'ゴミ箱' },
  // 2026-08-14 (2차) — '회사 공유' → '회사 문서'. 화면이 뜻하는 것이 "공유 동작" 이 아니라
  // "회사가 내보이는 문서 모음" 이라 이름을 바꿨다(위치도 '공유됨' 바로 아래로 옮겼다).
  'workspace.nav.company': { ko: '회사 문서', en: 'Company docs', ja: '全社文書' },
  'workspace.nav.folders': { ko: '폴더', en: 'Folders', ja: 'フォルダー' },
  'workspace.view.home_sub': { ko: '최근 수정한 순', en: 'Recently modified', ja: '最近更新した順' },
  'workspace.view.shared_sub': { ko: '나에게 공유된 항목', en: 'Shared with me', ja: '自分と共有' },
  'workspace.view.trash_sub': { ko: '삭제한 항목', en: 'Deleted items', ja: '削除した項目' },
  'workspace.view.company_sub': { ko: '관리자만 올릴 수 있습니다', en: 'Only admins can upload here', ja: '管理者のみアップロードできます' },
  'workspace.view.empty': { ko: '표시할 항목이 없습니다.', en: 'Nothing to show.', ja: '表示する項目がありません。' },
  /*
   * '내 파일' 루트가 비었을 때 (2026-09-02).
   *
   * 이 화면은 2026-09-02 부터 **워크스페이스에 둔 것만** 보여 준다. 그전까지는 채팅·메일·
   * 프로젝트 첨부까지 전부 떴으므로, 첨부만 있던 사람에게는 어느 날 갑자기 목록이 텅 빈다.
   * 「표시할 항목이 없습니다」만 놓으면 **파일이 지워진 줄 안다** — 어디로 갔는지 적는다.
   */
  'workspace.view.empty_my_root': {
    ko: '워크스페이스에 올리거나 저장한 파일이 여기 모입니다. 채팅·메일·프로젝트에 붙인 첨부는 각 화면에 그대로 있고, 차지하는 용량은 아래 「용량 관리」에서 볼 수 있습니다.',
    en: 'Files you upload or save to the workspace appear here. Attachments in chat, mail and projects stay on those screens — see “Manage storage” below for the space they use.',
    ja: 'ワークスペースにアップロード・保存したファイルがここに集まります。チャット・メール・プロジェクトの添付は各画面にそのまま残り、使用容量は下の「容量管理」で確認できます。',
  },
  'workspace.trash.restore': { ko: '복원', en: 'Restore', ja: '復元' },
  'workspace.trash.purge': { ko: '영구 삭제', en: 'Delete forever', ja: '完全に削除' },
  'workspace.trash.empty_all': { ko: '휴지통 비우기', en: 'Empty trash', ja: 'ゴミ箱を空にする' },
  'workspace.trash.confirm_purge': { ko: '"{name}" 을(를) 영구 삭제합니다. 되돌릴 수 없습니다.', en: '"{name}" will be deleted forever. This cannot be undone.', ja: '「{name}」を完全に削除します。元に戻せません。' },
  'workspace.trash.confirm_empty': { ko: '휴지통을 비웁니다. 되돌릴 수 없습니다.', en: 'Empty the trash? This cannot be undone.', ja: 'ゴミ箱を空にします。元に戻せません。' },
  // 2026-08-14 — 휴지통 체크 선택(복원·영구삭제 일괄).
  'workspace.trash.deleted_at': { ko: '삭제일', en: 'Deleted', ja: '削除日' },
  'workspace.trash.confirm_purge_many': {
    ko: '선택한 {count}개를 영구 삭제합니다. 되돌릴 수 없습니다.',
    en: '{count} selected items will be deleted forever. This cannot be undone.',
    ja: '選択した {count} 件を完全に削除します。元に戻せません。',
  },
  'workspace.trash.bulk_failed': {
    ko: '{count}개는 처리하지 못했습니다.',
    en: 'Failed to process {count} items.',
    ja: '{count} 件は処理できませんでした。',
  },
  'workspace.trash.restored_to_root': { ko: '원래 폴더가 없어 최상위로 복원했습니다.', en: 'The original folder is gone — restored to the top level.', ja: '元のフォルダーがないため最上位に復元しました。' },
  'workspace.folder_upload.heading': { ko: '폴더 업로드', en: 'Upload folder', ja: 'フォルダーをアップロード' },
  'workspace.folder_upload.label_pick': { ko: '올릴 폴더 선택', en: 'Choose a folder', ja: 'アップロードするフォルダー' },
  'workspace.folder_upload.hint': { ko: '폴더 안의 하위 폴더 구조가 그대로 만들어집니다.', en: 'The folder structure inside is recreated as-is.', ja: '中のフォルダー構成がそのまま再現されます。' },
  'workspace.folder_upload.creating': { ko: '폴더 {done}/{total} 만드는 중…', en: 'Creating folders {done}/{total}…', ja: 'フォルダーを作成中 {done}/{total}…' },

  // ===== 2026-08-14 — 파일 사용처 · 스마트 폴더 · 삭제 경고 · 공유 관리 =====
  'workspace.col.usage': { ko: '사용처', en: 'Used in', ja: '使用先' },
  'workspace.usage.chat': { ko: '채팅', en: 'Chat', ja: 'チャット' },
  'workspace.usage.task': { ko: '프로젝트', en: 'Project', ja: 'プロジェクト' },
  'workspace.usage.mail': { ko: '메일', en: 'Mail', ja: 'メール' },
  'workspace.usage.calendar': { ko: '일정', en: 'Calendar', ja: '予定' },
  'workspace.usage.board': { ko: '게시글', en: 'Post', ja: '投稿' },
  /*
   * 2026-10-07 — 서버가 이미 세는 둘(#2218). 꼴은 형제들처럼 **맨낱말**이다(채팅·게시글·회의록).
   *
   * ⭐ `contact` = 「명함첩」 — 그릇을 가리키는 말이다(2026-10-01 사용자 결정: 그릇 = 명함첩 ·
   *   알맹이 = 연락처). `workspace.smart.contact`(「명함첩 사진」)와 **같은 어휘**를 쓴다.
   *
   * ⭐⭐ `ecard` = 「전자명함」 — ⛔ **내가 지은 낱말이 아니다.** `admin_menu_registry` 의
   *   «모바일 전용» 메뉴 넷(음성 녹음 · **전자명함** · 통합 검색 · 알림) 중 하나이고,
   *   2026-09-15 사용자 결정(REQUEST-6 R6-12)으로 그렇게 불린다(`lib/menu-config.ts:42` ·
   *   콘솔 `MenuControls.tsx:21`). ⇒ ★ 그래서 이것은 **이름을 짓는 일이 아니라 재사용**이다.
   *
   * ⇒ ⚠ **그래서 `contacts-i18n.ts` 의 ⏸ ⓑ 동결을 «열지 않는다».** 그 동결은 「**명함**」이라는
   *   맨낱말이 ⓐ(남의 한 장)와 ⓑ(내 명함 · vCard) 사이에서 헷갈리는 것에 대한 것이고,
   *   `mail-i18n` 의 `vcard*` 키들이 그 대상이다. 「전자명함」은 **그 둘과 다른, 이미 쓰이는 낱말**
   *   이라 어느 쪽을 고르는 일이 아니다.
   *   ⇒ ⛔ 그래도 **그 메뉴 이름이 바뀌면 이 줄도 따라간다** — 출처가 거기이기 때문이다.
   */
  'workspace.usage.contact': { ko: '명함첩', en: 'Contacts', ja: 'アドレス帳' },
  'workspace.usage.ecard': { ko: '전자명함', en: 'E-card', ja: '電子名刺' },
  'workspace.usage.none': { ko: '쓰이지 않음', en: 'Not used', ja: '未使用' },
  // 후보 출처의 짧은 배지 이름 (2026-08-19) — 서버가 켜는 즉시 배지가 제대로 나오게.
  'workspace.usage.minutes': { ko: '회의록', en: 'Minutes', ja: '議事録' },
  'workspace.usage.approval': { ko: '결재', en: 'Approval', ja: '決裁' },
  'workspace.usage.reservation': { ko: '예약', en: 'Reservation', ja: '予約' },
  'workspace.usage.memo': { ko: '메모', en: 'Memo', ja: 'メモ' },
  'workspace.detail.usage': { ko: '사용처', en: 'Used in', ja: '使用先' },
  'workspace.detail.usage_none': {
    ko: '어디에도 쓰이지 않습니다. 지워도 다른 화면에 영향이 없습니다.',
    en: 'Not used anywhere. Deleting it will not affect other screens.',
    ja: 'どこでも使われていません。削除しても他の画面に影響しません。',
  },
  'workspace.row.preview': { ko: '미리보기', en: 'Preview', ja: 'プレビュー' },
  'workspace.row.aria_preview': { ko: '{name} 미리보기', en: 'Preview {name}', ja: '{name} をプレビュー' },
  'workspace.row.unshare': { ko: '공유 해제', en: 'Stop sharing', ja: '共有を解除' },
  'workspace.row.confirm_unshare': {
    ko: '"{name}" 의 공유를 모두 해제합니다.',
    en: 'Remove all sharing for "{name}"?',
    ja: '「{name}」の共有をすべて解除します。',
  },
  'workspace.smart.aria': { ko: '출처별 폴더', en: 'Folders by source', ja: '出所別フォルダー' },
  'workspace.smart.heading': { ko: '출처별 모음', en: 'By source', ja: '出所別' },
  'workspace.smart.chat': { ko: '채팅 첨부', en: 'Chat files', ja: 'チャット添付' },
  'workspace.smart.task': { ko: '프로젝트 첨부', en: 'Project files', ja: 'プロジェクト添付' },
  'workspace.smart.mail': { ko: '메일 첨부', en: 'Mail files', ja: 'メール添付' },
  'workspace.smart.calendar': { ko: '일정 첨부', en: 'Calendar files', ja: '予定添付' },
  'workspace.smart.board': { ko: '게시글 첨부', en: 'Post files', ja: '投稿添付' },
  // 2026-10-07 — ⚠ 형제들의 「… 첨부」 꼴이 «아니다». 명함 사진은 첨부가 아니라 «그 명함 자체»다.
  //   (바로 아래 `none`(「정리 대상」)도 그 꼴을 벗어나 있다 — 이 묶음의 잣대는 「첨부인가」가 아니라
  //    「무엇으로 쓰이는 파일인가」다.) ⇒ ko 는 그릇을 가리키는 「명함첩」이다(2026-10-01 결정).
  'workspace.smart.contact': { ko: '명함첩 사진', en: 'Card photos', ja: '名刺写真' },
  // 2026-10-07 — 「전자명함」은 이미 살아 있는 메뉴 이름이다(위 workspace.usage.ecard 주석 참조).
  'workspace.smart.ecard': { ko: '전자명함 사진', en: 'E-card photos', ja: '電子名刺の写真' },
  'workspace.smart.none': { ko: '정리 대상', en: 'Unused', ja: '未使用' },

  // ── 아직 서버가 세지 않는 출처 후보 (2026-08-19) ──
  // 서버가 참조처를 켜는 순간 자동으로 목록에 뜨는데, 그때 이름이 없으면 키가 그대로
  // 노출된다. 미리 넣어 두는 비용은 0이고 빠졌을 때의 값은 마이너스다.
  'workspace.smart.minutes': { ko: '회의록 첨부', en: 'Minutes files', ja: '議事録の添付' },
  'workspace.smart.approval': { ko: '결재 첨부', en: 'Approval files', ja: '決裁の添付' },
  'workspace.smart.reservation': { ko: '예약 첨부', en: 'Reservation files', ja: '予約の添付' },
  'workspace.smart.memo': { ko: '메모 첨부', en: 'Memo files', ja: 'メモの添付' },
  // 좌측 '내 파일' 하위 첫 항목 — 출처로 거르지 않은 전체.
  'workspace.smart.all': { ko: '전체', en: 'All files', ja: 'すべて' },
  'workspace.nav.shared_with_me': { ko: '나에게 공유됨', en: 'Shared with me', ja: '自分と共有' },
  'workspace.nav.shared_by_me': { ko: '내가 공유함', en: 'Shared by me', ja: '自分が共有' },
  'workspace.col.share_target': { ko: '공유 대상', en: 'Shared with', ja: '共有先' },
  'workspace.share.target_more': { ko: '{name} 외 {count}', en: '{name} +{count}', ja: '{name} ほか{count}' },
  'workspace.delwarn.aria': { ko: '삭제 영향 확인', en: 'Delete impact', ja: '削除の影響' },
  'workspace.delwarn.heading': { ko: '다른 곳에서 사용 중입니다', en: 'Still in use elsewhere', ja: '他の場所で使用中です' },
  'workspace.delwarn.lead': {
    ko: '{count}개 파일이 다른 화면에 첨부돼 있습니다.',
    en: '{count} file(s) are attached elsewhere.',
    ja: '{count}件のファイルが他の画面に添付されています。',
  },
  'workspace.delwarn.effect': {
    ko: '삭제하면 그 화면의 첨부는 목록에 그대로 남지만 열 수 없게 됩니다. 휴지통에서 복원하면 다시 열립니다.',
    en: 'After deleting, those attachments stay listed but can no longer be opened. Restoring from the trash brings them back.',
    ja: '削除すると、その添付は一覧に残りますが開けなくなります。ゴミ箱から復元すれば再び開けます。',
  },
  'workspace.delwarn.clean': {
    ko: '나머지 {count}개는 어디에도 쓰이지 않아 영향이 없습니다.',
    en: 'The other {count} are unused and unaffected.',
    ja: '残り{count}件はどこでも使われておらず影響ありません。',
  },
  'workspace.delwarn.more': { ko: '외 {count}곳', en: '+{count} more', ja: 'ほか{count}件' },
  'workspace.delwarn.confirm': { ko: '알겠습니다, 삭제', en: 'Delete anyway', ja: '了解、削除' },
  'workspace.purge.in_use': {
    ko: '{count}개는 다른 곳에서 사용 중이라 남겨 두었습니다.',
    en: '{count} item(s) were kept because they are still in use.',
    ja: '{count}件は使用中のため残しました。',
  },

  // ===== 2026-08-14 — 드라이브 표 · 행 메뉴 · 선택 툴바 · 세부정보 =====
  'workspace.col.name': { ko: '이름', en: 'Name', ja: '名前' },
  'workspace.col.modified': { ko: '수정일', en: 'Modified', ja: '更新日' },
  'workspace.col.shared_at': { ko: '공유일', en: 'Shared', ja: '共有日' },
  'workspace.col.size': { ko: '파일 크기', en: 'Size', ja: 'サイズ' },
  'workspace.col.visibility': { ko: '공개 여부', en: 'Visibility', ja: '公開範囲' },
  'workspace.col.sharer': { ko: '공유한 사람', en: 'Shared by', ja: '共有者' },
  'workspace.vis.private': { ko: '비공개', en: 'Private', ja: '非公開' },
  'workspace.vis.team': { ko: '팀 공유', en: 'Team', ja: 'チーム共有' },
  'workspace.vis.company': { ko: '전사 공유', en: 'Company', ja: '全社共有' },
  // 2026-10-27 — 직접 걸었거나 상위 폴더에서 물려받은 공유가 있는 행.
  'workspace.vis.shared': { ko: '공유', en: 'Shared', ja: '共有' },
  'workspace.share.inherited_heading': { ko: '상위 폴더에서 받은 공유', en: 'From parent folders', ja: '上位フォルダから' },
  'workspace.share.inherited_from': { ko: '「{name}」에서', en: 'via "{name}"', ja: '「{name}」から' },
  'workspace.row.aria_menu': { ko: '{name} 메뉴', en: '{name} menu', ja: '{name} メニュー' },
  'workspace.row.aria_select': { ko: '{name} 선택', en: 'Select {name}', ja: '{name} を選択' },
  'workspace.row.aria_select_all': { ko: '전체 선택', en: 'Select all', ja: 'すべて選択' },
  'workspace.row.share': { ko: '공유', en: 'Share', ja: '共有' },
  'workspace.row.access': { ko: '액세스 관리', en: 'Manage access', ja: 'アクセス管理' },
  'workspace.row.download': { ko: '다운로드', en: 'Download', ja: 'ダウンロード' },
  'workspace.row.copy_link': { ko: '링크 복사', en: 'Copy link', ja: 'リンクをコピー' },
  'workspace.row.rename': { ko: '이름 바꾸기', en: 'Rename', ja: '名前を変更' },
  'workspace.row.move': { ko: '다음으로 이동', en: 'Move to', ja: '移動' },
  'workspace.row.copy_to': { ko: '다음으로 복사', en: 'Copy to', ja: 'コピー' },
  'workspace.row.copy_to_mine': { ko: '내 워크스페이스로 복사', en: 'Copy to my workspace', ja: 'マイワークスペースにコピー' },
  'workspace.row.duplicate': { ko: '복제', en: 'Duplicate', ja: '複製' },
  'workspace.row.color': { ko: '폴더 색상', en: 'Folder colour', ja: 'フォルダーの色' },
  'workspace.row.detail': { ko: '세부정보', en: 'Details', ja: '詳細' },
  'workspace.row.delete': { ko: '삭제', en: 'Delete', ja: '削除' },
  'workspace.row.confirm_delete': { ko: '"{name}" 을(를) 휴지통으로 보냅니다.', en: 'Move "{name}" to the trash?', ja: '「{name}」をゴミ箱に移動します。' },
  'workspace.row.rename_prompt': { ko: '새 이름', en: 'New name', ja: '新しい名前' },
  'workspace.row.link_copied': { ko: '링크를 복사했습니다.', en: 'Link copied.', ja: 'リンクをコピーしました。' },
  'workspace.toolbar.sort': { ko: '정렬', en: 'Sort', ja: '並べ替え' },
  'workspace.toolbar.view': { ko: '보기', en: 'View', ja: '表示' },
  'workspace.toolbar.detail': { ko: '세부정보', en: 'Details', ja: '詳細' },
  'workspace.sort.aria': { ko: '정렬 메뉴', en: 'Sort menu', ja: '並べ替えメニュー' },
  'workspace.sort.by': { ko: '정렬 기준', en: 'Sort by', ja: '並べ替え基準' },
  'workspace.sort.direction': { ko: '방향', en: 'Direction', ja: '方向' },
  'workspace.sort.name': { ko: '이름순', en: 'Name', ja: '名前順' },
  'workspace.sort.type': { ko: '유형', en: 'Type', ja: '種類' },
  'workspace.sort.updated': { ko: '수정일자', en: 'Modified', ja: '更新日' },
  'workspace.sort.size': { ko: '파일크기', en: 'Size', ja: 'サイズ' },
  'workspace.sort.asc': { ko: '오름차순', en: 'Ascending', ja: '昇順' },
  'workspace.sort.desc': { ko: '내림차순', en: 'Descending', ja: '降順' },
  'workspace.view.aria': { ko: '보기 메뉴', en: 'View menu', ja: '表示メニュー' },
  'workspace.view.list': { ko: '목록', en: 'List', ja: 'リスト' },
  'workspace.view.compact': { ko: '간단한 목록', en: 'Compact list', ja: 'コンパクト' },
  'workspace.view.tile': { ko: '타일', en: 'Tiles', ja: 'タイル' },
  'workspace.sel.aria': { ko: '선택 항목 작업', en: 'Selection actions', ja: '選択項目の操作' },
  'workspace.sel.clear': { ko: '선택 해제', en: 'Clear selection', ja: '選択を解除' },
  'workspace.sel.count': { ko: '{count}개 선택', en: '{count} selected', ja: '{count}件選択' },
  'workspace.detail.aria': { ko: '세부정보 패널', en: 'Details panel', ja: '詳細パネル' },
  'workspace.detail.close': { ko: '세부정보 닫기', en: 'Close details', ja: '詳細を閉じる' },
  'workspace.detail.access': { ko: '액세스', en: 'Access', ja: 'アクセス' },
  'workspace.detail.owner': { ko: '소유자', en: 'Owner', ja: '所有者' },
  'workspace.detail.activity': { ko: '활동 이력', en: 'Activity', ja: 'アクティビティ' },
  'workspace.detail.props': { ko: '속성', en: 'Properties', ja: 'プロパティ' },
  'workspace.detail.format': { ko: '형식', en: 'Type', ja: '種類' },
  'workspace.detail.created': { ko: '만든 날짜', en: 'Created', ja: '作成日' },
  'workspace.detail.path': { ko: '경로', en: 'Location', ja: '場所' },
  'workspace.detail.no_shares': { ko: '아직 공유하지 않았습니다.', en: 'Not shared yet.', ja: 'まだ共有していません。' },
  'workspace.detail.no_activity': { ko: '기록된 활동이 없습니다.', en: 'No activity recorded.', ja: '記録された操作はありません。' },
  'workspace.detail.unknown_target': { ko: '(삭제된 대상)', en: '(deleted target)', ja: '(削除済み)' },
  'workspace.detail.empty_hint': { ko: '파일이나 폴더를 선택하면 액세스 · 활동 이력 · 속성이 여기에 나옵니다.', en: 'Select a file or folder to see its access, activity and properties here.', ja: 'ファイルやフォルダーを選ぶと、アクセス・アクティビティ・プロパティが表示されます。' },
  'workspace.detail.multi': { ko: '여러 항목 선택', en: 'Multiple items', ja: '複数選択' },
  'workspace.detail.multi_hint': { ko: '{count}개를 선택했습니다. 세부정보는 하나만 선택했을 때 보여 줍니다.', en: '{count} items selected. Details show for a single item.', ja: '{count}件選択。詳細は1件選択時に表示されます。' },
  'workspace.act.uploaded': { ko: '님이 업로드', en: ' uploaded', ja: ' がアップロード' },
  'workspace.act.saved': { ko: '님이 저장', en: ' saved', ja: ' が保存' },
  'workspace.act.renamed': { ko: '님이 이름 변경', en: ' renamed', ja: ' が名前を変更' },
  'workspace.act.moved': { ko: '님이 이동', en: ' moved', ja: ' が移動' },
  'workspace.act.copied': { ko: '님이 복사', en: ' copied', ja: ' がコピー' },
  'workspace.act.deleted': { ko: '님이 삭제', en: ' deleted', ja: ' が削除' },
  'workspace.act.restored': { ko: '님이 복원', en: ' restored', ja: ' が復元' },
  'workspace.act.shared': { ko: '님이 공유', en: ' shared', ja: ' が共有' },
  'workspace.act.unshared': { ko: '님이 공유 해제', en: ' unshared', ja: ' が共有を解除' },
  'workspace.act.folder_created': { ko: '님이 폴더 생성', en: ' created a folder', ja: ' がフォルダーを作成' },
  'workspace.act.other': { ko: '님의 작업', en: ' made a change', ja: ' が操作' },
  'workspace.share.aria': { ko: '공유 설정', en: 'Share settings', ja: '共有設定' },
  'workspace.share.heading': { ko: '공유 · {name}', en: 'Share · {name}', ja: '共有 · {name}' },
  'workspace.share.tab_add': { ko: '공유 추가', en: 'Add', ja: '共有を追加' },
  'workspace.share.tab_manage': { ko: '액세스 관리 ({count})', en: 'Access ({count})', ja: 'アクセス管理 ({count})' },
  'workspace.share.scope_label': { ko: '공유 범위', en: 'Share with', ja: '共有範囲' },
  'workspace.share.scope_company': { ko: '회사 전체 직원', en: 'Everyone at the company', ja: '全社員' },
  'workspace.share.scope_company_desc': { ko: '같은 회사의 모든 구성원이 볼 수 있습니다', en: 'Everyone in your company can see it', ja: '社内の全員が閲覧できます' },
  'workspace.share.scope_team': { ko: '팀 공유', en: 'A team', ja: 'チーム共有' },
  'workspace.share.scope_team_desc': { ko: '선택한 팀의 구성원만 접근합니다', en: 'Only members of the chosen team', ja: '選んだチームのメンバーのみ' },
  'workspace.share.scope_user': { ko: '특정 구성원', en: 'Specific people', ja: '特定のメンバー' },
  'workspace.share.scope_user_desc': { ko: '이름으로 직접 지정합니다', en: 'Pick people by name', ja: '名前で指定します' },
  'workspace.share.pick_team': { ko: '팀 선택', en: 'Choose a team', ja: 'チームを選択' },
  'workspace.share.pick_team_placeholder': { ko: '팀을 고르세요', en: 'Select a team', ja: 'チームを選んでください' },
  'workspace.share.pick_user': { ko: '구성원 검색', en: 'Find a person', ja: 'メンバーを検索' },
  'workspace.share.pick_user_placeholder': { ko: '이름으로 검색', en: 'Search by name', ja: '名前で検索' },
  'workspace.share.perm_label': { ko: '권한', en: 'Permission', ja: '権限' },
  'workspace.share.perm_view': { ko: '보기', en: 'Can view', ja: '閲覧' },
  'workspace.share.perm_edit': { ko: '편집', en: 'Can edit', ja: '編集' },
  'workspace.share.btn_add': { ko: '공유', en: 'Share', ja: '共有' },
  'workspace.share.btn_busy': { ko: '적용 중…', en: 'Applying…', ja: '適用中…' },
  'workspace.share.remove': { ko: '{name} 공유 해제', en: 'Remove {name}', ja: '{name} の共有を解除' },
  'workspace.share.err_pick_team': { ko: '공유할 팀을 고르세요.', en: 'Choose a team to share with.', ja: '共有するチームを選んでください。' },
  'workspace.share.err_pick_user': { ko: '공유할 구성원을 고르세요.', en: 'Choose a person to share with.', ja: '共有するメンバーを選んでください。' },
  'workspace.share.include_subfolders': { ko: '하위 폴더', en: 'Subfolders', ja: 'サブフォルダ' },
  'workspace.share.subfolders_on': { ko: '하위 폴더 포함', en: 'Incl. subfolders', ja: 'サブフォルダを含む' },
  'workspace.share.subfolders_off': { ko: '이 폴더만', en: 'This folder only', ja: 'このフォルダのみ' },
  'workspace.share.link_copied': { ko: '링크를 복사했습니다.', en: 'Link copied.', ja: 'リンクをコピーしました。' },
  'workspace.picker.move_heading': { ko: '이동할 위치 선택', en: 'Move to…', ja: '移動先を選択' },
  'workspace.picker.copy_heading': { ko: '복사할 위치 선택', en: 'Copy to…', ja: 'コピー先を選択' },
  'workspace.picker.subject': { ko: '{name} 외 {count}개', en: '{name} and {count} item(s)', ja: '{name} ほか {count}件' },
  'workspace.picker.no_subfolder': { ko: '하위 폴더가 없습니다.', en: 'No subfolders here.', ja: 'サブフォルダーがありません。' },
  'workspace.picker.blocked_self': { ko: '자기 자신 안으로는 옮길 수 없습니다.', en: 'Cannot move into itself.', ja: '自身の中へは移動できません。' },
  /*
   * 2026-08-18 — 옛 `btn_here`('{name} 에 넣기')를 **이름 없는 단순 라벨**로 바꿨다.
   * 폴더 이름이 길면 버튼이 그만큼 길어지는데 모달은 400px 고정 + overflow:hidden 이라
   * 취소 버튼이 밖으로 밀려 잘렸다. 어디에 넣는지는 바로 위 경로(breadcrumb)가 말한다.
   */
  'workspace.picker.btn_move': { ko: '이동', en: 'Move', ja: '移動' },
  'workspace.picker.btn_copy': { ko: '복사', en: 'Copy', ja: 'コピー' },
  'workspace.picker.btn_busy': { ko: '처리 중…', en: 'Working…', ja: '処理中…' },
  'workspace.color.heading': { ko: '폴더 색상 · {name}', en: 'Folder colour · {name}', ja: 'フォルダーの色 · {name}' },
  'workspace.color.aria': { ko: '색상 팔레트', en: 'Colour palette', ja: 'カラーパレット' },
  'workspace.color.swatch': { ko: '색상 {n}', en: 'Colour {n}', ja: '色 {n}' },
  'workspace.color.none': { ko: '색 없음', en: 'None', ja: 'なし' },
  'workspace.color.apply': { ko: '적용', en: 'Apply', ja: '適用' },
  'workspace.bulk.done': { ko: '{count}개 처리 완료', en: '{count} item(s) done', ja: '{count}件完了' },
  'workspace.bulk.partial': { ko: '{ok}개 성공 · {fail}개 실패', en: '{ok} succeeded · {fail} failed', ja: '{ok}件成功 · {fail}件失敗' },

  // ===== 2026-08-12 — 유형 필터 + 검색 =====
  'workspace.kind.all': { ko: '전체', en: 'All', ja: 'すべて' },
  'workspace.kind.word': { ko: 'Word', en: 'Word', ja: 'Word' },
  'workspace.kind.excel': { ko: 'Excel', en: 'Excel', ja: 'Excel' },
  'workspace.kind.ppt': { ko: 'PPT', en: 'PPT', ja: 'PPT' },
  // 2026-08-14 — 드라이브화로 필터 5종 → 9종. 예전에는 아래 넷이 전부 '그외' 였다.
  'workspace.kind.pdf': { ko: 'PDF', en: 'PDF', ja: 'PDF' },
  'workspace.kind.markup': { ko: '마크업', en: 'Markup', ja: 'マークアップ' },
  'workspace.kind.txt': { ko: 'txt', en: 'txt', ja: 'txt' },
  'workspace.kind.image': { ko: '이미지', en: 'Images', ja: '画像' },
  'workspace.kind.etc': { ko: '그외', en: 'Other', ja: 'その他' },
  'workspace.filter.aria': { ko: '파일 유형 필터', en: 'File type filter', ja: 'ファイル種別フィルター' },
  'workspace.search.placeholder': {
    ko: '파일명·문서 제목 검색',
    en: 'Search by file name or document title',
    ja: 'ファイル名・文書タイトルで検索',
  },
  'workspace.search.aria': { ko: '파일 검색', en: 'Search files', ja: 'ファイル検索' },
  'workspace.search.clear': { ko: '검색어 지우기', en: 'Clear search', ja: '検索語を消す' },
  'workspace.search.scope_hint': {
    ko: '전체 스토리지에서 검색 중',
    en: 'Searching all storage',
    ja: 'ストレージ全体を検索中',
  },
  'workspace.search.empty': {
    ko: '"{q}" 와 일치하는 파일이 없습니다',
    en: 'No files match "{q}"',
    ja: '"{q}" に一致するファイルがありません',
  },
  'workspace.list.btn_more': { ko: '더 보기', en: 'Load more', ja: 'もっと見る' },
  'workspace.list.loading_more': { ko: '불러오는 중…', en: 'Loading…', ja: '読み込み中...' },
  // 번호 페이지네이션 (2026-08-21) — '더 보기' 를 대체한다.
  'workspace.page.aria': { ko: '목록 페이지', en: 'List pages', ja: 'リストページ' },
  'workspace.page.prev': { ko: '이전 페이지', en: 'Previous page', ja: '前のページ' },
  'workspace.page.next': { ko: '다음 페이지', en: 'Next page', ja: '次のページ' },
  'workspace.page.goto': { ko: '{n} 페이지', en: 'Page {n}', ja: '{n} ページ' },
  'workspace.page.size': { ko: '쪽당', en: 'Per page', ja: '1ページ' },
  // 출처별 모음 행 (2026-08-21) — 목록 맨 위의 가상 폴더.
  'workspace.src.count': { ko: '{n}개', en: '{n} files', ja: '{n} 件' },
  'workspace.src.heading': { ko: '출처별 모음', en: 'By source', ja: '出所別' },
  'workspace.page.summary': { ko: '{total}개 중 {from}–{to}', en: '{from}–{to} of {total}', ja: '{total} 件中 {from}–{to}' },

  // ===== 2026-08-12 — 스토리지 사용량 (placeholder 대체) =====
  'workspace.storage.meta_personal': { ko: '개인 스토리지', en: 'Personal storage', ja: '個人ストレージ' },
  'workspace.storage.unlimited': { ko: '무제한', en: 'Unlimited', ja: '無制限' },
  'workspace.storage.remaining': { ko: '{size} 남음', en: '{size} left', ja: '残り {size}' },
  'workspace.storage.warn': {
    ko: '용량이 거의 찼습니다 — 관리자에게 한도 상향을 요청하세요',
    en: 'Storage is nearly full — ask an administrator to raise the limit',
    ja: '容量がほぼ一杯です — 管理者に上限引き上げを依頼してください',
  },
  // ===== 2026-08-12 — 인앱 문서 패널 / 편집기 =====
  'workspace.editor.edit': { ko: '편집', en: 'Edit', ja: '編集' },
  'workspace.editor.save': { ko: '저장', en: 'Save', ja: '保存' },
  'workspace.editor.saving': { ko: '저장 중…', en: 'Saving…', ja: '保存中...' },
  'workspace.editor.close_editor': { ko: '편집 닫기', en: 'Close editor', ja: '編集を閉じる' },
  'workspace.editor.download': { ko: '다운로드', en: 'Download', ja: 'ダウンロード' },
  'workspace.editor.unsaved': { ko: '저장하지 않은 변경', en: 'Unsaved changes', ja: '未保存の変更' },
  'workspace.editor.rename_title': { ko: '이름 바꾸기', en: 'Rename', ja: '名前を変更' },
  'workspace.editor.rename_failed': {
    ko: '이름을 바꾸지 못했습니다',
    en: 'Could not rename',
    ja: '名前を変更できませんでした',
  },
  'workspace.editor.confirm_discard': {
    ko: '저장하지 않은 변경이 있습니다. 그대로 닫으면 변경 내용이 사라집니다. 닫을까요?',
    en: 'You have unsaved changes. Closing now will discard them. Close anyway?',
    ja: '未保存の変更があります。閉じると失われます。閉じますか？',
  },
  'workspace.editor.load_failed': {
    ko: '편집기를 불러오지 못했습니다',
    en: 'Failed to load the editor',
    ja: 'エディターを読み込めませんでした',
  },
  'workspace.editor.download_failed': { ko: '다운로드 실패', en: 'Download failed', ja: 'ダウンロード失敗' },
  // "다른 이름으로 저장" — 웹에는 저장 대화가 없어 **같은 폴더에 새 파일**을 만든다.
  // 패널 제목은 원본 그대로라 알려주지 않으면 어느 파일을 고치고 있는지 알 수 없다.
  'workspace.editor.saved_as_new': {
    ko: '새 문서로 저장했습니다: {name}\n이후 저장은 새 문서에 적용됩니다.',
    en: 'Saved as a new document: {name}\nSubsequent saves apply to the new document.',
    ja: '新しい文書として保存しました: {name}\n以降の保存は新しい文書に適用されます。',
  },
  // 웹에는 "이름을 정해 저장" 대화가 없어(파일 입력은 열기 전용) 앱이 직접 이름을 묻는다.
  // 경량 미리보기(읽기 전용) — 편집기 셸 없이 엔진만으로 그린다.
  'workspace.editor.hwp_preview_loading': {
    ko: '문서를 그리는 중…', en: 'Rendering document…', ja: '文書を描画中…',
  },
  'workspace.editor.hwp_open_edit': {
    ko: '편집하려면 편집을 누르세요', en: 'Click Edit to make changes', ja: '編集するには「編集」を押してください',
  },
  'workspace.editor.save_as_name': {
    ko: '새 문서 이름을 입력하세요',
    en: 'Enter a name for the new document',
    ja: '新しい文書の名前を入力してください',
  },
  'workspace.editor.export_name': {
    ko: '내보낼 파일 이름을 입력하세요 (같은 폴더에 만들어집니다)',
    en: 'Enter a file name to export (created in the same folder)',
    ja: 'エクスポートするファイル名を入力してください（同じフォルダーに作成されます）',
  },
  'workspace.editor.unsupported': {
    ko: '이 형식은 미리보기를 지원하지 않습니다. 다운로드해 확인해 주세요.',
    en: 'Preview is not available for this format. Please download the file.',
    ja: 'この形式はプレビューに対応していません。ダウンロードしてご確認ください。',
  },
  // 한글 문서(.hwp/.hwpx)는 경량 미리보기 렌더러가 없다 — 편집기가 곧 뷰어다.
  // "지원하지 않는 형식" 은 사실이 아니므로(열 수 있다) 무엇을 누르면 되는지 알려 준다.
  'workspace.editor.hwp_open_hint': {
    ko: '한글 문서는 편집기에서 열립니다.',
    en: 'Hancom Office (HWP) documents open in the editor.',
    ja: 'ハングル文書はエディターで開きます。',
  },
  // ===== 2026-08-14 — 텍스트 편집기 · 이미지 미리보기 =====
  'workspace.editor.text_area': { ko: '텍스트 편집', en: 'Text editor', ja: 'テキスト編集' },
  'workspace.editor.text_save_failed': {
    ko: '저장하지 못했습니다',
    en: 'Failed to save',
    ja: '保存できませんでした',
  },
  'workspace.editor.text_too_large': {
    ko: '{size} 가 넘는 텍스트는 미리보기를 생략합니다. 다운로드해 확인해 주세요.',
    en: 'Text larger than {size} is not previewed. Please download the file.',
    ja: '{size} を超えるテキストはプレビューしません。ダウンロードしてご確認ください。',
  },
  'workspace.editor.open_full': {
    ko: '전체 화면으로 보기',
    en: 'Open full screen',
    ja: '全画面で表示',
  },
  // ----- 편집기 AI 코파일럿 (2026-08-15, PR2) -----
  // ⚠ `workspace.ai.title` 은 화면 문구이자 **CSS `content:` 값**이다 — 편집기 안 상류
  //   패널의 제목("Genspark")을 덮는 데 쓰인다(`--wsai-title`). 따옴표는 호출부가 붙인다.
  'workspace.ai.title': { ko: 'AI 어시스턴트', en: 'AI assistant', ja: 'AI アシスタント' },
  'workspace.ai.open': {
    ko: 'AI 어시스턴트 열기',
    en: 'Open AI assistant',
    ja: 'AI アシスタントを開く',
  },
  'workspace.ai.close': {
    ko: 'AI 어시스턴트 닫기',
    en: 'Close AI assistant',
    ja: 'AI アシスタントを閉じる',
  },
  'workspace.ai.preparing': {
    ko: 'AI 어시스턴트 준비 중',
    en: 'Preparing the AI assistant',
    ja: 'AI アシスタントを準備中',
  },
  // ----- 호스트 폴백 카드 — 한글(HWP)·평문 (2026-08-15, PR3) -----
  // 이 둘에는 편집기 안에 AI 가 없다. 카드를 호스트가 직접 그리고 **도구 없이**(강등 모드)
  // 답변·초안만 낸다 — 무엇이 되고 무엇이 안 되는지를 빈 화면에서 먼저 말한다.
  'workspace.ai.no_edit_note': {
    ko: '이 형식에서는 AI 가 문서를 직접 고칠 수 없습니다. 내용을 묻거나 초안을 받아 붙여 넣으세요.',
    en: 'AI cannot edit this format directly. Ask about the content or paste a draft it writes.',
    ja: 'この形式では AI が文書を直接編集できません。内容を尋ねるか、下書きを受け取って貼り付けてください。',
  },
  // 본문은 못 고치지만 파일 이름 같은 공통 도구는 있는 경우(한글, 2026-08-15).
  // 두 사실을 한 줄에 담아야 한다 — "고칠 수 없다" 만 말하면 할 수 있는 일까지 안 시킨다.
  'workspace.ai.no_edit_note_tools': {
    ko: 'AI 가 문서 본문은 직접 고칠 수 없습니다. 초안을 받아 붙여 넣으세요. 파일 이름은 AI 가 바꿀 수 있습니다.',
    en: 'AI cannot edit the document body here — paste a draft it writes. It can still rename the file.',
    ja: 'AI は本文を直接編集できません。下書きを受け取って貼り付けてください。ファイル名は AI が変更できます。',
  },
  // 도구를 쓸 수 있는 편집기(평문 등)에서 no_edit_note 대신 뜬다 — "고칠 수 없다" 를
  // 그대로 두면 할 수 있는 일을 사용자가 시도하지 않는다.
  'workspace.ai.edit_note': {
    ko: 'AI 가 이 문서를 직접 고칠 수 있습니다. 고칠 내용을 말하면 편집기에 바로 반영합니다.',
    en: 'AI can edit this document directly. Describe the change and it lands in the editor.',
    ja: 'AI がこの文書を直接編集できます。変更内容を伝えるとエディターに反映されます。',
  },
  // 빈 상태의 제목 — 상류 다섯 패널과 같은 규격(제목 + 설명 2단, 2026-08-15 Phase D1).
  'workspace.ai.empty_title': {
    ko: '무엇을 도와드릴까요?',
    en: 'How can I help?',
    ja: '何をお手伝いしましょうか？',
  },
  // 컴포저 아래 줄 — 다섯 패널의 `AiComposer` 힌트와 같은 자리.
  'workspace.ai.hint_send': {
    ko: 'Enter 로 전송 · Shift+Enter 줄바꿈',
    en: 'Enter to send · Shift+Enter for a new line',
    ja: 'Enter で送信 · Shift+Enter で改行',
  },
  'workspace.ai.hint_busy': { ko: '답변하는 중…', en: 'Responding…', ja: '応答中…' },
  'workspace.ai.working': { ko: '작업 중…', en: 'Working…', ja: '作業中…' },
  'workspace.ai.steps_done': { ko: '작업 {n}단계', en: '{n} steps', ja: '{n} ステップ' },
  'workspace.ai.undo': { ko: '되돌리기', en: 'Undo', ja: '元に戻す' },
  'workspace.ai.undone': { ko: '되돌렸습니다', en: 'Reverted', ja: '元に戻しました' },
  'workspace.ai.edited': { ko: '문서를 수정했습니다', en: 'Document edited', ja: '文書を編集しました' },
  'workspace.ai.placeholder': {
    ko: '문서에 대해 물어보세요',
    en: 'Ask about this document',
    ja: 'この文書について質問してください',
  },
  'workspace.ai.send': { ko: '보내기', en: 'Send', ja: '送信' },
  'workspace.ai.stop': { ko: '중지', en: 'Stop', ja: '停止' },
  'workspace.ai.thinking': { ko: '생각 중…', en: 'Thinking…', ja: '考え中…' },
  'workspace.ai.copy': { ko: '복사', en: 'Copy', ja: 'コピー' },
  'workspace.ai.copied': { ko: '복사했습니다', en: 'Copied', ja: 'コピーしました' },
  'workspace.ai.insert': { ko: '편집기에 삽입', en: 'Insert into editor', ja: 'エディターに挿入' },
  'workspace.ai.inserted': { ko: '삽입했습니다', en: 'Inserted', ja: '挿入しました' },
  'workspace.ai.failed': {
    ko: 'AI 응답을 받지 못했습니다.',
    en: 'No response from the AI.',
    ja: 'AI の応答を受け取れませんでした。',
  },
  // 문서 글을 못 읽는 경우가 실재한다 — 편집기가 뜨는 중이거나, 본문이 표·글상자에만 있는
  // 문서다. 조용히 두면 사용자는 "AI 가 문서를 봤다" 고 믿고 답을 신뢰한다.
  'workspace.ai.no_context': {
    ko: '문서 내용을 읽지 못해 일반 지식으로만 답합니다.',
    en: 'The document text could not be read, so answers rely on general knowledge only.',
    ja: '文書の本文を読み取れないため、一般知識のみで回答します。',
  },
  'workspace.ai.context_trimmed': {
    ko: '문서가 길어 앞부분만 참고합니다.',
    en: 'The document is long, so only the beginning is used.',
    ja: '文書が長いため、冒頭のみを参照します。',
  },
  'workspace.editor.media_failed': {
    ko: '재생할 수 없는 파일입니다. 다운로드해 확인해 주세요.',
    en: 'This media cannot be played. Please download the file.',
    ja: '再生できないファイルです。ダウンロードしてご確認ください。',
  },
  'workspace.editor.image_failed': {
    ko: '이미지를 불러오지 못했습니다',
    en: 'Failed to load the image',
    ja: '画像を読み込めませんでした',
  },
  // ===== 2026-08-14 — 회사 공유 폴더 읽기 전용 =====
  'workspace.company.read_only': { ko: '읽기 전용', en: 'Read-only', ja: '読み取り専用' },
  'workspace.company.read_only_hint': {
    ko: '회사 공유 폴더는 관리자만 바꿀 수 있습니다',
    en: 'Only administrators can change the company shared folder',
    ja: '会社共有フォルダーは管理者のみ変更できます',
  },
  // ===== 2026-08-14 — 회사공유 등록(관리자가 자기 '내 파일' 에서 전사 공개) =====
  'workspace.company.register': {
    ko: '회사공유에 등록',
    en: 'Publish to company',
    ja: '全社共有に登録',
  },
  'workspace.company.unregister': {
    ko: '회사공유에서 내리기',
    en: 'Remove from company',
    ja: '全社共有から外す',
  },
  'workspace.company.confirm_register': {
    ko: '"{name}" 을(를) 회사공유에 등록할까요? 전 직원이 볼 수 있게 됩니다.',
    en: 'Publish "{name}" to the company? Everyone will be able to see it.',
    ja: '「{name}」を全社共有に登録しますか？ 全社員が閲覧できるようになります。',
  },
  'workspace.company.confirm_unregister': {
    ko: '"{name}" 을(를) 회사공유에서 내릴까요? 파일은 내 파일에 그대로 남습니다.',
    en: 'Remove "{name}" from the company? The file stays in your files.',
    ja: '「{name}」を全社共有から外しますか？ ファイルはマイファイルに残ります。',
  },
  'workspace.company.empty': {
    ko: '아직 회사공유에 등록된 자료가 없습니다',
    en: 'Nothing has been published to the company yet',
    ja: 'まだ全社共有に登録された資料はありません',
  },
  'workspace.storage.over': {
    ko: '한도를 초과했습니다 — 새 업로드가 차단됩니다',
    en: 'Limit exceeded — new uploads are blocked',
    ja: '上限を超えました — 新規アップロードはブロックされます',
  },

  // ----- 2026-08-14 (2차) 사용자 요구 -----
  // hwp 독립 칩 · 한글 새 문서 · 컬럼 정렬 · 용량 관리 · HTML 미리보기 · 썸네일 레일.
  'workspace.kind.hwp': { ko: '한글', en: 'Hangul', ja: 'ハングル' },
  'workspace.create.hwp': { ko: '한글 문서', en: 'Hangul document', ja: 'ハングル文書' },

  // 컬럼 머리글 정렬 — 머리글 자체가 버튼이 된다.
  'workspace.col.sort_aria': {
    ko: '{col} 기준 정렬',
    en: 'Sort by {col}',
    ja: '{col} で並べ替え',
  },

  // 용량 관리
  'workspace.storage.manage': { ko: '용량관리', en: 'Manage storage', ja: '容量管理' },
  'workspace.storage.manage_heading': { ko: '용량 관리', en: 'Storage management', ja: '容量管理' },
  'workspace.storage.manage_hint': {
    ko: '폴더별 사용량입니다. 하위 폴더의 용량까지 합해서 보여 줍니다.',
    en: 'Usage per folder, including everything inside it.',
    ja: 'フォルダー別の使用量です。配下のフォルダーも合算して表示します。',
  },
  'workspace.storage.bucket_root': { ko: '폴더에 없는 파일', en: 'Files outside folders', ja: 'フォルダー外のファイル' },
  'workspace.storage.bucket_unknown': { ko: '(이름 없는 폴더)', en: '(unnamed folder)', ja: '(名前のないフォルダー)' },
  'workspace.storage.bucket_files': { ko: '{count}개', en: '{count} files', ja: '{count}件' },
  'workspace.storage.empty_btn': { ko: '비우기', en: 'Empty', ja: '空にする' },
  'workspace.storage.confirm_empty': {
    ko: '"{name}" 안의 파일 {count}개를 휴지통으로 보낼까요? 휴지통에서 되돌릴 수 있습니다.',
    en: 'Move {count} files in "{name}" to the trash? You can restore them from there.',
    ja: '「{name}」内のファイル {count}件をゴミ箱へ移動しますか？ ゴミ箱から戻せます。',
  },
  'workspace.storage.emptied': {
    ko: '{count}개를 휴지통으로 보냈습니다',
    en: 'Moved {count} items to the trash',
    ja: '{count}件をゴミ箱へ移動しました',
  },
  'workspace.storage.manage_empty': {
    ko: '용량을 차지하는 파일이 없습니다',
    en: 'Nothing is taking up space',
    ja: '容量を使っているファイルはありません',
  },
  'workspace.storage.truncated': {
    ko: '파일이 너무 많아 일부만 집계했습니다',
    en: 'Too many files — only part of them was counted',
    ja: 'ファイルが多すぎるため一部のみ集計しました',
  },
  'workspace.storage.manage_failed': {
    ko: '용량 정보를 불러오지 못했습니다',
    en: 'Failed to load storage details',
    ja: '容量情報を取得できませんでした',
  },

  // 용량 관리 범위 (2026-08-14 2차) — 개인 · 팀 · 회사.
  'workspace.storage.scope_aria': { ko: '사용량 범위', en: 'Usage scope', ja: '使用量の範囲' },
  'workspace.storage.scope_personal': { ko: '내 사용량', en: 'Mine', ja: '自分' },
  'workspace.storage.scope_team': { ko: '팀', en: 'Team', ja: 'チーム' },
  'workspace.storage.scope_company': { ko: '회사 전체', en: 'Company', ja: '全社' },
  'workspace.storage.pick_team': { ko: '팀 선택', en: 'Pick a team', ja: 'チーム選択' },
  'workspace.storage.readonly_hint': {
    ko: '팀·회사 범위는 보기 전용입니다 — 남이 올린 파일이 함께 집계됩니다.',
    en: 'Team and company scopes are read-only — they include files uploaded by others.',
    ja: 'チーム・全社の範囲は閲覧専用です — 他の人がアップロードしたファイルも含みます。',
  },

  // HTML 미리보기 — 소스가 아니라 화면으로 그린다.
  'workspace.preview.html_aria': { ko: 'HTML 미리보기', en: 'HTML preview', ja: 'HTML プレビュー' },
  'workspace.preview.html_source': { ko: '소스 보기', en: 'View source', ja: 'ソースを見る' },
  'workspace.preview.html_rendered': { ko: '화면으로 보기', en: 'View rendered', ja: '表示で見る' },
} satisfies Record<string, Record<Lang, string>>;

export type WorkspaceI18nKey = keyof typeof MESSAGES;

/**
 * Cycle 52 — WorkspacePage i18n lookup.
 * cycle 46 학습 phase 5 fallback chain (lang → en → ko) 적용.
 * cycle 57 — 선택적 {param} 치환 (calendar-i18n 와 동일 패턴).
 */
export function tWorkspace(key: WorkspaceI18nKey, params?: Record<string, string | number>): string {
  const m = MESSAGES[key];
  if (!m) return key;
  const raw = m[getSharedLang()] ?? m.en ?? m[FALLBACK_LANG] ?? key;
  if (!params) return raw;
  return raw.replace(/\{(\w+)\}/g, (_, name) => {
    const v = params[name];
    return v === undefined || v === null ? `{${name}}` : String(v);
  });
}

// base-i18n thin wrapper (cycle 41 통합 패턴 일관)
export function getWorkspaceLang(): Lang { return getSharedLang(); }
export function setWorkspaceLang(value: Lang): void { setSharedLang(value); }
export function subscribeWorkspaceLang(fn: () => void): () => void {
  return subscribeSharedLang(fn);
}
